import React from 'react';
import styled from '@emotion/styled';
import { Text } from 'src/components';
import { colors } from 'src/theme';

export interface ToggleOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface ToggleButtonGroupProps {
  options: ToggleOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  width: '50%',
});

const Label = styled.label({
  fontFamily: "'Red Hat Text', sans-serif",
  fontSize: 16,
  fontWeight: 400,
  lineHeight: '20px',
  color: colors.text1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const GAP = 4;

const ToggleContainer = styled.div({
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  padding: 4,
  height: 40,
  background: colors.background6,
  borderRadius: 8,
  width: 'fit-content',
  position: 'relative',
  boxSizing: 'border-box',
});

const Inner = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  gap: GAP,
  position: 'relative'
});

const Thumb = styled.div<{ left: number; width: number }>(({ left, width }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: `${left}px`,
  width: `${width}px`,
  transition: 'left 0.25s ease, width 0.25s ease',
  background: colors.white,
  borderRadius: 4,
  boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
  pointerEvents: 'none',
}));

const OptionButton = styled.button<{ disabled?: boolean }>(({ disabled }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  flex: '0 0 auto',
  whiteSpace: 'nowrap',
  minWidth: 0,
  padding: '8px 16px',
  background: 'transparent',
  border: 'none',
  borderRadius: 4,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: '"Red Hat Display", sans-serif',
  position: 'relative',
  zIndex: 1,
  opacity: disabled ? 0.6 : 1,
}));

export const ToggleButtonGroup: React.FC<ToggleButtonGroupProps> = ({
  options,
  value,
  onChange,
  label,
  disabled = false,
  className,
}) => {
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));
  const innerRef = React.useRef<HTMLDivElement>(null);
  const [thumbRect, setThumbRect] = React.useState({ left: 0, width: 0 });

  React.useLayoutEffect(() => {
    const inner = innerRef.current;
    if (!inner || options.length === 0) return;
    const buttons = inner.querySelectorAll<HTMLButtonElement>('button');
    const selected = buttons[selectedIndex];
    if (!selected) return;
    const innerRect = inner.getBoundingClientRect();
    const btnRect = selected.getBoundingClientRect();
    setThumbRect({
      left: btnRect.left - innerRect.left,
      width: btnRect.width,
    });
  }, [selectedIndex, options]);

  React.useLayoutEffect(() => {
    const inner = innerRef.current;
    if (!inner) return () => {};
    const ro = new ResizeObserver(() => {
      if (!inner || options.length === 0) return;
      const buttons = inner.querySelectorAll<HTMLButtonElement>('button');
      const idx = Math.max(0, options.findIndex((o) => o.value === value));
      const selected = buttons[idx];
      if (!selected) return;
      const innerRect = inner.getBoundingClientRect();
      const btnRect = selected.getBoundingClientRect();
      setThumbRect({
        left: btnRect.left - innerRect.left,
        width: btnRect.width,
      });
    });
    ro.observe(inner);
    return () => ro.disconnect();
  }, [options, value]);

  const handleSelect = (optionValue: string) => {
    if (!disabled) {
      onChange(optionValue);
    }
  };

  return (
    <Container className={className}>
      {label != null && label !== '' && <Label>{label}</Label>}
      <ToggleContainer>
        <Inner ref={innerRef}>
          <Thumb left={thumbRect.left} width={thumbRect.width} />
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <OptionButton
                key={option.value}
                type="button"
                disabled={disabled}
                onClick={() => handleSelect(option.value)}
              >
                <Text type="base" color={isSelected ? colors.text1 : colors.text4}>
                  {option.label}
                </Text>
                {option.icon != null ? option.icon : null}
              </OptionButton>
            );
          })}
        </Inner>
      </ToggleContainer>
    </Container>
  );
};
